import React from "react";
import { Loader2 } from "lucide-react";
import { useFundDashboardController } from "./useFundDashboardController";
import { useFundValidationRulesController } from "../fund-validation-rules/useFundValidationRulesController";
import { Badge } from "@/components/ui/badge";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { useAtomValue } from "jotai";
import { ReportingPeriodTimeline } from "../reporting-periods";
import moment from "moment-timezone";
import {
  NavCard,
  ExceptionsCard,
  TransactionsHoldingsCard,
  PeriodChecklistCard,
  RecentExceptionsSection,
  ActiveValidationRulesSection,
  UnconfirmedItemsSection,
  AnalyticsChart,
} from "./components";

export const FundDashboard = () => {
  const controller = useFundDashboardController();
  const validationRulesController = useFundValidationRulesController();
  const selectedFund = useAtomValue(SelectedFundAtom);

  // Loading state
  if (controller.isLoading) {
    return (
      <div>
        <ReportingPeriodTimeline />
        <div className="flex items-center justify-center h-[400px]">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
            <p className="text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  // No fund or reporting period selected
  if (!selectedFund || !controller.selectedReportingPeriod) {
    return (
      <div>
        <ReportingPeriodTimeline />
        <div className="flex flex-col items-center gap-2 mt-10">
          <p className="text-muted-foreground">
            Please select a fund and reporting period to view the dashboard.
          </p>
        </div>
      </div>
    );
  }

  // At this point, we know selectedFund and selectedReportingPeriod are defined
  const fundName = controller.selectedFund?.name || selectedFund.name;
  console.log(controller.navData);

  return (
    <div className="space-y-6">
      <ReportingPeriodTimeline />
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">{fundName}</h2>
        <Badge variant="outline" className="text-sm">
          {moment(controller.selectedReportingPeriod.period).format(
            "D MMMM YYYY"
          )}
        </Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <NavCard navData={controller.navData} />
        
        <ExceptionsCard
          exceptionCount={controller.exceptionCount}
          criticalExceptionCount={controller.criticalExceptionCount}
          highExceptionCount={controller.highExceptionCount}
          mediumExceptionCount={controller.mediumExceptionCount}
          lowExceptionCount={controller.lowExceptionCount}
          criticalExceptionPercentage={controller.criticalExceptionPercentage}
          highExceptionPercentage={controller.highExceptionPercentage}
        />

        <TransactionsHoldingsCard
          totalTransactions={controller.totalTransactions}
          confirmedTransactions={controller.confirmedTransactions}
          unconfirmedTransactions={controller.unconfirmedTransactions}
          totalHoldings={controller.totalHoldings}
          confirmedHoldings={controller.confirmedHoldings}
          unconfirmedHoldings={controller.unconfirmedHoldings}
        />

        <PeriodChecklistCard
          entityUploads={controller.entityUploads}
          uploadCompletionPercentage={controller.uploadCompletionPercentage}
        />
      </div>

      <AnalyticsChart
        historicalNavData={controller.historicalNavData}
        isLoading={controller.isLoading}
      />

      <RecentExceptionsSection
        exceptions={controller.exceptions}
        exceptionCount={controller.exceptionCount}
      />

      <ActiveValidationRulesSection
        validationRules={validationRulesController.validationRules}
        isLoading={validationRulesController.isLoading}
        error={validationRulesController.error}
      />

      <UnconfirmedItemsSection
        unconfirmedTransactions={controller.unconfirmedTransactions}
        totalTransactions={controller.totalTransactions}
        unconfirmedHoldings={controller.unconfirmedHoldings}
        totalHoldings={controller.totalHoldings}
      />
    </div>
  );
};
